import * as React from "react";
import { ImageUploader, type ImageRejection } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

// A 2x2 solid-teal PNG as a data URI — stands in for a saved remote logo so the
// preview / replace / remove states render without a network fetch (the same
// stand-in the component's stories use).
const SAMPLE_IMAGE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAACCAYAAABytg0kAAAAEklEQVR42mNk+M9Qz0BkYBxVSAYAQ7gB/3T0ZRkAAAAASUVORK5CYII=";

export function ImageUploaderDemo() {
  // Avatar logo well, controlled — the unified-modal swap. Seeded empty so the
  // initial-letter fallback shows; picking a file reveals the hover overlay.
  const [logo, setLogo] = React.useState<File | string | null>(null);

  // The interactive dropzone drives the `upload` persistence seam: a picked
  // file dims + spins (uploading) and then resolves to a returned URL (done).
  // The stub stands in for a real `api.uploadCover(file)`.
  const [cover, setCover] = React.useState<File | string | null>(null);
  const fakeUpload = React.useCallback(
    (_file: File) =>
      new Promise<string>((resolve) => setTimeout(() => resolve(SAMPLE_IMAGE), 1400)),
    [],
  );

  // The rejection block surfaces the headless onReject seam: a too-large file
  // (maxSizeBytes:1) is refused inline and reported here, which a real consumer
  // would route to a toast.
  const [lastReject, setLastReject] = React.useState<ImageRejection | null>(null);

  return (
    <Section
      id="image-uploader"
      title="ImageUploader"
      description="Drag-drop / click image input with object-URL preview, type + size validation, and a pixel-reveal into the preview. Composes the Image primitive (loading skeleton + onError fallback) and a controlled PixelCard for the reveal; consolidates the platform's four hand-rolled pickers into one leak-free seam that creates and revokes its own object URL, resets the input so re-picking the same file re-fires, and reports rejections outward via onReject instead of toasting from inside."
    >
      <VariantBlock label="Dropzone" caption="the default cover-upload surface; drag a file or click to browse">
        <div className="w-[420px] max-w-full">
          <ImageUploader variant="dropzone" hint="JPG, PNG, WebP • Max 5MB" />
        </div>
      </VariantBlock>

      <VariantBlock label="Avatar well" caption="controlled logo well — initial-letter fallback, hover the preview for the change overlay">
        <div className="flex items-center gap-6">
          <ImageUploader variant="avatar" value={logo} onChange={setLogo} name="Acme" />
          <ImageUploader variant="avatar" shape="circle" size="lg" name="Ada Lovelace" />
          <ImageUploader variant="avatar" shape="circle" size="lg" value={SAMPLE_IMAGE} name="Ada" />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Upload lifecycle"
        caption="pick a file — the upload seam dims + spins, then resolves to the saved URL"
      >
        <div className="flex w-[420px] max-w-full flex-col gap-2">
          <ImageUploader
            variant="dropzone"
            value={cover}
            onChange={setCover}
            upload={fakeUpload}
            accept={["image/jpeg", "image/png", "image/webp", "image/svg+xml"]}
            hint="JPG, PNG, WebP, SVG • Max 5MB"
          />
          <p className="text-xs text-muted-foreground">
            upload(file) resolves after a tick — a real consumer returns the persisted URL.
          </p>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Validation"
        caption="maxSizeBytes:1 refuses everything to show the inline error + destructive tint"
      >
        <div className="flex w-[420px] max-w-full flex-col gap-2">
          <ImageUploader
            variant="dropzone"
            maxSizeBytes={1}
            onReject={(rejections) => setLastReject(rejections[0] ?? null)}
          />
          <p className="text-xs text-muted-foreground">
            {lastReject
              ? `onReject received (${lastReject.code}): ${lastReject.message}`
              : "onReject reports here — a real consumer would toast it."}
          </p>
        </div>
      </VariantBlock>
    </Section>
  );
}
